import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "../hr/theme";
import { Btn, Pill } from "../hr/ui";
import { DataTable, cmoney, num, dt } from "./crmui";
import { toast } from "sonner";
import { RefreshCw, GitMerge, SplitSquareHorizontal } from "lucide-react";

export default function Duplicates() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [compare, setCompare] = useState(null);
  const [surviving, setSurviving] = useState("");

  const load = useCallback(() => {
    api.get("/crm/duplicates", { params: { status } }).then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    try {
      const r = await api.post("/crm/duplicates/scan", {});
      toast.success(`${r.new_candidates} new candidate(s) · ${r.open} open`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const open = async (row) => {
    try {
      const c = await api.get(`/crm/duplicates/${row.id}/compare`);
      setCompare(c); setSurviving(c.candidate.primary_customer_id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const merge = async () => {
    try {
      const r = await api.post(`/crm/duplicates/${compare.candidate.id}/merge`, { surviving_customer_id: surviving });
      toast.success(`Merged — ${r.points_moved} points transferred to the surviving record`);
      setCompare(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const keep = async () => {
    try {
      await api.post(`/crm/duplicates/${compare.candidate.id}/keep-separate`, { notes: "Reviewed — genuinely different customers" });
      toast.success("Marked as separate customers");
      setCompare(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const FIELDS = [
    ["name", "Name"], ["phone", "Phone"], ["email", "Email"], ["registered_at", "Registered"],
    ["location_name", "Location"], ["orders", "Orders"], ["spend", "Lifetime spend"],
    ["points_available", "Loyalty points"], ["registration_channel", "Source"],
    ["verification_status", "Verification"],
  ];

  return (
    <div className="space-y-4" data-testid="crm-duplicates-page">
      <div className="flex flex-wrap items-center gap-2">
        {["open", "merged", "kept_separate"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`dup-tab-${s}`}
            className="h-9 px-3 rounded-md text-[12.5px] font-semibold border"
            style={status === s ? { background: HC.burgundy, color: "#fff", borderColor: HC.burgundy } : { background: "#fff", color: HC.text, borderColor: HC.border }}>
            {s.replace("_", " ")}
          </button>
        ))}
        <div className="flex-1" />
        <Btn onClick={scan} data-testid="dup-scan"><RefreshCw size={14} /> Scan for duplicates</Btn>
      </div>

      <DataTable testid="crm-duplicates-table" rows={rows}
        columns={[["match_type", "Matched on"], ["match_value", "Identifier"], ["primary_name", "Primary record"],
                  ["duplicate_name", "Possible duplicate"], ["confidence", "Confidence"], ["detected_at", "Detected"], ["status", "Status"]]}
        render={{
          primary_name: (r) => r.primary?.name || "—",
          duplicate_name: (r) => r.duplicate?.name || "—",
          confidence: (r) => `${Math.round((r.confidence || 0) * 100)}%`,
          detected_at: (r) => dt(r.detected_at),
          status: (r) => <Pill status={r.status === "open" ? "pending" : "approved"} text={r.status} />,
        }}
        onRowClick={open} empty="No duplicate candidates — run a scan to check" />

      {compare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-3xl p-5 space-y-4" style={{ borderColor: HC.border }} data-testid="dup-compare-modal">
            <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>Compare accounts</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest" style={{ color: HC.soft }}>
                    <th className="px-3 py-2">Field</th>
                    {compare.records.map((r) => (
                      <th key={r.id} className="px-3 py-2">
                        <label className="flex items-center gap-2">
                          <input type="radio" checked={surviving === r.id} onChange={() => setSurviving(r.id)}
                            data-testid={`dup-keep-${r.role}`} />
                          Keep this ({r.role})
                        </label>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FIELDS.map(([k, label]) => (
                    <tr key={k} className="border-t" style={{ borderColor: HC.borderSoft }}>
                      <td className="px-3 py-2 text-[12px] font-semibold" style={{ color: HC.muted }}>{label}</td>
                      {compare.records.map((r) => (
                        <td key={r.id + k} className="px-3 py-2 text-[12.5px]" style={{ color: HC.text }}>
                          {k === "spend" ? cmoney(r[k]) : k === "registered_at" ? dt(r[k])
                            : k === "points_available" ? num(r[k]) : (r[k] ?? "—")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[11.5px]" style={{ color: HC.soft }}>
              Merging moves orders, loyalty history, activity, identities and consent to the surviving record, transfers the point balance through the ledger, and keeps the source account as an audited tombstone — nothing is deleted.
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="outline" onClick={() => setCompare(null)}>Cancel</Btn>
              <Btn variant="outline" onClick={keep} data-testid="dup-keep-separate"><SplitSquareHorizontal size={14} /> Keep separate</Btn>
              <Btn onClick={merge} data-testid="dup-merge"><GitMerge size={14} /> Merge accounts</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
